import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgApexchartsModule } from 'ng-apexcharts';
import { MaterialModule } from 'src/app/material.module';
import { SharedModule } from 'src/app/shared/shared.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { Store } from '@ngrx/store';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { forkJoin, of } from 'rxjs';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PermissionService } from 'src/app/core/services/permission.service';
import {
  SuperAdminDashboardService,
  DashboardStats,
  AgentPerformanceItem,
  RecentActivityItem,
  TenantDropdownItem,
} from 'src/app/core/services/super-admin-dashboard.service';
import { CardPeriodOption } from 'src/app/shared/card-title/card-title.component';
import { FormsModule } from '@angular/forms';
import { ListModulePaginationComponent } from 'src/app/shared/components/list-module/list-module-pagination.component';
import type { ListModulePaginationMeta } from 'src/app/shared/components/list-module/list-module.types';
import { paginateClientRecords } from 'src/app/shared/utils/client-list.util';
import { PlatformHealthCardComponent } from './platform-health-card/platform-health-card.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SharedModule,
    MaterialModule,
    NgApexchartsModule,
    IconModule,
    NgScrollbarModule,
    PageSurfaceHeaderComponent,
    ListModulePaginationComponent,
    RouterLink,
    PlatformHealthCardComponent,
  ],
})
export class DashboardComponent implements OnInit {
  readonly agentPerformancePeriodOptions: CardPeriodOption[] = [
    { value: '7d', label: 'Last 7 days' },
    { value: '15d', label: 'Last 15 days' },
    { value: '30d', label: 'Last 30 days' },
  ];

  revenueChart: any;
  weeklyActivityChart: any;
  pdStatusChart: any;

  isLoading = true;
  store: any;

  // Tenant filter
  tenants: TenantDropdownItem[] = [];
  selectedTenantId: number | undefined = undefined;
  selectedPeriod = '30d';

  // KPI data
  stats: DashboardStats | null = null;

  // Table data
  agentPerformance: AgentPerformanceItem[] = [];

  /**
   * The table pages client-side: the endpoint returns every agent for the period in one payload,
   * and the chart above the table reads the same array for its top twelve.
   */
  agentPage = 1;
  readonly agentPerPage = 10;

  get pagedAgentPerformance(): AgentPerformanceItem[] {
    return paginateClientRecords(this.agentPerformance, this.agentPage, this.agentPerPage).pageRows;
  }

  get agentPaginationMeta(): ListModulePaginationMeta {
    return paginateClientRecords(this.agentPerformance, this.agentPage, this.agentPerPage).meta;
  }

  onAgentPageChange(page: number): void {
    this.agentPage = page;
  }
  recentActivity: RecentActivityItem[] = [];

  private readonly permissionService = inject(PermissionService);
  readonly slugs = SUPER_ADMIN_PERMISSION_SLUGS;

  /** Cross-tenant filter — only meaningful to someone allowed to see the tenant list. */
  get canViewTenantFilter(): boolean {
    return this.permissionService.can(this.slugs.TENANT_LIST);
  }

  get canViewAgentPerformance(): boolean {
    return this.permissionService.can(this.slugs.AGENT_LIST);
  }

  /** "Active Agents" and "Agent Accuracy%" are agent-module data, not PD data. */
  get canViewAgentKpis(): boolean {
    return this.permissionService.can(this.slugs.AGENT_LIST);
  }

  /** KPI row shrinks from 5 to 3 columns when the agent cards are hidden. */
  get kpiGridClass(): string {
    return this.canViewAgentKpis
      ? 'xl:grid-cols-5 lg:grid-cols-5'
      : 'xl:grid-cols-3 lg:grid-cols-3';
  }

  /** PD KPIs, both charts and the recent-activity feed are all PD session data. */
  get canViewPdData(): boolean {
    return this.permissionService.can(this.slugs.PD_SESSION_LIST);
  }

  get canViewRecentActivity(): boolean {
    return this.canViewPdData;
  }

  constructor(
    public storeData: Store<any>,
    private readonly dashboardService: SuperAdminDashboardService,
  ) {}

  ngOnInit(): void {
    void this.initStore();
    // Permissions decide which panels load, so they must resolve before the first
    // fetch — otherwise an empty permission set would permanently skip the calls.
    this.permissionService.ensureLoaded().subscribe(() => {
      this.loadTenants();
      this.loadDashboard();
    });
  }

  loadTenants(): void {
    if (!this.canViewTenantFilter) {
      return;
    }
    this.dashboardService.getTenants().subscribe({
      next: (res) => { this.tenants = res.data ?? []; },
    });
  }

  loadDashboard(): void {
    this.isLoading = true;
    const tenantId = this.selectedTenantId;
    const period = this.selectedPeriod;

    // Panels the user cannot see are not fetched at all.
    forkJoin({
      stats: this.dashboardService.getStats(tenantId),
      weekly: this.canViewPdData
        ? this.dashboardService.getWeeklyActivity(tenantId, period)
        : of(null),
      agents: this.canViewAgentPerformance
        ? this.dashboardService.getAgentPerformance(tenantId, period)
        : of(null),
      activity: this.canViewRecentActivity
        ? this.dashboardService.getRecentActivity(tenantId)
        : of(null),
    }).subscribe({
      next: ({ stats, weekly, agents, activity }) => {
        this.stats = stats.data;
        this.agentPerformance = agents?.data ?? [];
        this.agentPage = 1;
        this.recentActivity = activity?.data ?? [];
        this.buildCharts(weekly?.data ?? null);
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; },
    });
  }

  onTenantChange(): void {
    this.loadDashboard();
  }

  onPeriodChange(period: string): void {
    this.selectedPeriod = period;
    this.loadDashboard();
  }

  private buildCharts(weekly: { labels: string[]; data: number[] } | null): void {
    this.storeData
      .select((d: any) => d.index)
      .subscribe((d: any) => {
        const isDark = d?.theme === 'dark' || d?.isDarkMode;
        this.initCharts(isDark, weekly);
      })
      .unsubscribe();
  }

  initCharts(isDark = false, weekly?: { labels: string[]; data: number[] } | null): void {
    const isRtl = false;

    // Agent Performance area chart
    this.revenueChart = {
      chart: {
        height: 425,
        type: 'area',
        fontFamily: 'Nunito, sans-serif',
        zoom: { enabled: false },
        toolbar: { show: false },
      },
      dataLabels: { enabled: false },
      stroke: { show: true, curve: 'smooth', width: 2, lineCap: 'square' },
      dropShadow: { enabled: true, opacity: 0.2, blur: 10, left: -7, top: 22 },
      colors: isDark ? ['#2196f3', '#e7515a'] : ['#1b55e2', '#e7515a'],
      labels: this.agentPerformance.slice(0, 12).map((a) => a.name ?? a.agent_code ?? `Agent ${a.id}`),
      xaxis: {
        axisBorder: { show: false },
        axisTicks: { show: false },
        crosshairs: { show: true },
        labels: { offsetX: isRtl ? 2 : 0, offsetY: 5, style: { fontSize: '10px', cssClass: 'apexcharts-xaxis-title' } },
      },
      yaxis: {
        tickAmount: 7,
        labels: {
          formatter: (v: number) => v + '',
          offsetX: isRtl ? -30 : -10,
          style: { fontSize: '10px', cssClass: 'apexcharts-yaxis-title' },
        },
        opposite: isRtl,
      },
      grid: {
        borderColor: isDark ? '#191e3a' : '#e0e6ed',
        strokeDashArray: 5,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
      },
      legend: { position: 'top', horizontalAlign: 'center', fontSize: '12px', markers: { width: 8, height: 8, offsetX: -2 }, itemMargin: { horizontal: 8, vertical: 5 } },
      tooltip: { marker: { show: true }, x: { show: false } },
      fill: { type: 'gradient', gradient: { shadeIntensity: 1, inverseColors: false, opacityFrom: isDark ? 0.19 : 0.28, opacityTo: 0.05, stops: isDark ? [100, 100] : [45, 100] } },
      series: [
        { name: 'Total PDs', data: this.agentPerformance.slice(0, 12).map((a) => a.total_pds) },
      ],
    };

    // Weekly Activity bar chart
    this.weeklyActivityChart = {
      chart: { height: 300, type: 'bar', fontFamily: 'Nunito, sans-serif', toolbar: { show: false } },
      plotOptions: { bar: { horizontal: false, columnWidth: '55%', borderRadius: 4, borderRadiusApplication: 'end' } },
      dataLabels: { enabled: false },
      stroke: { show: true, width: 0, colors: ['transparent'] },
      colors: ['#8B5CF6'],
      xaxis: {
        categories: weekly?.labels ?? [],
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: '12px', fontFamily: 'Nunito, sans-serif' } },
      },
      yaxis: {
        min: 0,
        labels: {
          formatter: (v: number) => v.toString(),
          style: { fontSize: '12px', fontFamily: 'Nunito, sans-serif' },
        },
      },
      grid: {
        borderColor: isDark ? '#374151' : '#E5E7EB',
        strokeDashArray: 0,
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
      },
      fill: { opacity: 1 },
      tooltip: { y: { formatter: (v: number) => v + ' sessions' } },
      series: [{ name: 'Sessions', data: weekly?.data ?? [] }],
    };

    // PD Status donut chart
    const byStatus = this.stats?.sessions.by_status ?? [];
    const completedCount = byStatus.find((s) => s.status === 'COMPLETED')?.count ?? 0;
    const inProgressCount = byStatus.find((s) => s.status === 'IN_PROGRESS')?.count ?? 0;
    const expiredCount = byStatus.find((s) => s.status === 'EXPIRED')?.count ?? 0;

    this.pdStatusChart = {
      chart: { type: 'donut', height: 300, fontFamily: 'Nunito, sans-serif', toolbar: { show: false } },
      series: [completedCount, inProgressCount, expiredCount],
      labels: ['Completed', 'In Progress', 'Expired'],
      colors: ['#5CB85C', '#FFD700', '#34495E'],
      dataLabels: { enabled: false },
      plotOptions: { pie: { donut: { size: '70%', background: 'transparent' } } },
      stroke: { show: true, width: 2, colors: ['#ffffff'] },
      legend: { show: false },
      tooltip: { y: { formatter: (v: number) => v.toString() } },
    };
  }

  async initStore() {
    this.storeData
      .select((d: any) => d.index)
      .subscribe((d: any) => {
        const hasChangeTheme = this.store?.theme !== d?.theme;
        const hasChangeLayout = this.store?.layout !== d?.layout;
        const hasChangeMenu = this.store?.menu !== d?.menu;
        const hasChangeSidebar = this.store?.sidebar !== d?.sidebar;

        this.store = d;

        if (hasChangeTheme || hasChangeLayout || hasChangeMenu || hasChangeSidebar) {
          if (this.isLoading || hasChangeTheme) {
            this.initCharts(d?.theme === 'dark' || d?.isDarkMode);
          } else {
            setTimeout(() => {
              this.initCharts(d?.theme === 'dark' || d?.isDarkMode);
            }, 300);
          }
        }
      });
  }

  get pdStatusCompletedPct(): string {
    const total = this.stats?.sessions.total ?? 0;
    if (!total) return '0';
    return ((this.stats!.sessions.completed / total) * 100).toFixed(0);
  }

  get pdStatusInProgressPct(): string {
    const total = this.stats?.sessions.total ?? 0;
    if (!total) return '0';
    const inProgress = this.stats?.sessions.by_status.find((s) => s.status === 'IN_PROGRESS')?.count ?? 0;
    return ((inProgress / total) * 100).toFixed(0);
  }

  get pdStatusExpiredPct(): string {
    const total = this.stats?.sessions.total ?? 0;
    if (!total) return '0';
    const expired = this.stats?.sessions.by_status.find((s) => s.status === 'EXPIRED')?.count ?? 0;
    return ((expired / total) * 100).toFixed(0);
  }

  get pdStatusCompleted(): number {
    return this.stats?.sessions.completed ?? 0;
  }

  get pdStatusInProgress(): number {
    return this.stats?.sessions.by_status.find((s) => s.status === 'IN_PROGRESS')?.count ?? 0;
  }

  get pdStatusExpired(): number {
    return this.stats?.sessions.by_status.find((s) => s.status === 'EXPIRED')?.count ?? 0;
  }

  private pctTrend(curr: number, prev: number): string {
    if (!prev && !curr) return '';
    if (!prev) return '+100';
    const pct = Math.round(((curr - prev) / prev) * 100);
    return pct >= 0 ? `+${pct}` : `${pct}`;
  }

  /** Active Agents card */
  get kpiActiveAgents(): string {
    return (this.stats?.kpis?.active_agents ?? 0).toString();
  }
  get kpiActiveAgentsBadge(): string {
    const n = this.stats?.kpis?.agents_new_this_month ?? 0;
    if (n > 0) {
      return `+${String(n).padStart(2, '0')}`;
    }
    if (n < 0) {
      return `${n}`;
    }
    return '';
  }

  /** PD Volume Today card */
  get kpiPdToday(): string {
    return (this.stats?.kpis?.pd_today ?? 0).toLocaleString();
  }
  get kpiPdTodayTrend(): string {
    const k = this.stats?.kpis;
    return k ? this.pctTrend(k.pd_today, k.pd_yesterday) : '';
  }

  /** Agent Accuracy% card */
  get kpiAccuracy(): string {
    const a = this.stats?.kpis?.accuracy_this_week;
    return a == null ? '—' : `${a}%`;
  }
  get kpiAccuracyTrend(): string {
    const k = this.stats?.kpis;
    if (k?.accuracy_this_week == null || k?.accuracy_prev_week == null) return '';
    const delta = Math.round((k.accuracy_this_week - k.accuracy_prev_week) * 10) / 10;
    return delta >= 0 ? `+${delta}` : `${delta}`;
  }

  /** Pending Policy Reviews card */
  get kpiPendingReviews(): string {
    return (this.stats?.kpis?.pending_reviews ?? 0).toString();
  }
  get kpiPendingReviewsTrend(): string {
    const k = this.stats?.kpis;
    return k ? this.pctTrend(k.pending_reviews, k.pending_reviews_yesterday) : '';
  }

  /** Average Time card */
  get kpiAvgTime(): string {
    const m = this.stats?.kpis?.avg_duration_this_week;
    return m == null ? '—' : `${m} Min`;
  }
  get kpiAvgTimeTrend(): string {
    const k = this.stats?.kpis;
    if (k?.avg_duration_this_week == null || k?.avg_duration_prev_week == null) return '';
    return this.pctTrend(k.avg_duration_this_week, k.avg_duration_prev_week);
  }
}
